import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { get } from 'lodash'
import * as am5 from '@amcharts/amcharts5'
import * as am5xy from '@amcharts/amcharts5/xy'
import { AMCHART_COLORS, NO_DATA_MESSAGE } from '../../utils/constants'
import { styled } from '@mui/material/styles'
import { Typography } from '@mui/material'
import { amchartsSetAriaLabel } from '../../utils/amcharts'

// Add amCharts license
am5.addLicense(process.env.GATSBY_AMCHARTSV5_LICENSE || '')

// Styled Components for MUI6
const FigureChart = styled('div')({
  padding: 0,
})

const ChartContent = styled('div')(({ theme }) => ({
  display: 'block',
  minHeight: '24rem',
  fontSize: '1rem',
  [theme.breakpoints.down('sm')]: {
    minHeight: '30rem',
  },
}))

const NoDataContainer = styled('div')(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: 'center',
}))

const PriceHistoryChart = ({ data }) => {
  const chartRef = useRef(null)
  const rootRef = useRef(null)

  useEffect(() => {
    // Restore original logic: simply get the priceHistory array.
    const priceHistory = get(data, 'priceHistory') || []

    if (priceHistory.length === 0) {
      return undefined
    }

    // Create root element for amCharts
    const root = am5.Root.new(chartRef.current)
    rootRef.current = root
    root.numberFormatter.set('numberFormat', '#.00')

    // Create main chart
    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: true,
        wheelX: 'panX',
        wheelY: 'zoomX',
        pinchZoomX: true,
        paddingLeft: 0,
        layout: root.verticalLayout,
        ariaLabel: 'Price History Chart',
      })
    )

    // Set base colors
    chart.get('colors').set(
      'colors',
      AMCHART_COLORS.map((color) => am5.color(color))
    )

    // Create X Axis (Date)
    const xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        maxDeviation: 0.2,
        baseInterval: {
          timeUnit: 'day',
          count: 1,
        },
        renderer: am5xy.AxisRendererX.new(root, {
          minorGridEnabled: true,
        }),
        tooltip: am5.Tooltip.new(root, {}),
      })
    )

    // Create Y Axis (Value)
    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {
          pan: 'zoom',
        }),
        tooltip: am5.Tooltip.new(root, {}),
      })
    )

    // Y axis label (NAV)
    yAxis.children.unshift(
      am5.Label.new(root, {
        y: am5.percent(50),
        centerY: am5.percent(50),
        text: 'NAV',
        textAlign: 'center',
        rotation: -90,
      })
    )

    // Add cursor for interactive behavior
    chart.set(
      'cursor',
      am5xy.XYCursor.new(root, {
        behavior: 'none',
        xAxis,
      })
    )

    // Add main series (LineSeries) for NAV values
    const series = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: 'Value',
        xAxis,
        yAxis,
        valueYField: 'nav',
        valueXField: 'date',
        tooltip: am5.Tooltip.new(root, {
          labelText: '${valueY}',
        }),
        legendRangeLabelText: '{name}',
        legendLabelText: '{name}: ${nav}',
      })
    )

    // Add legend to the chart
    const legend = chart.children.push(
      am5.Legend.new(root, {
        x: am5.percent(50),
        centerX: am5.percent(50),
      })
    )
    legend.data.setAll(chart.series.values)

    // Add horizontal scrollbar
    const scrollbarX = chart.set(
      'scrollbarX',
      am5xy.XYChartScrollbar.new(root, {
        orientation: 'horizontal',
        height: 60,
      })
    )
    chart.bottomAxesContainer.children.push(scrollbarX)

    // Set scrollbar colors
    scrollbarX.chart.get('colors').set(
      'colors',
      AMCHART_COLORS.map((color) => am5.color(color))
    )

    // Scrollbar axes
    const sbxAxis = scrollbarX.chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        maxDeviation: 0.2,
        baseInterval: {
          timeUnit: 'day',
          count: 1,
        },
        renderer: am5xy.AxisRendererX.new(root, {
          minorGridEnabled: true,
        }),
      })
    )

    const sbyAxis = scrollbarX.chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
      })
    )

    // Scrollbar series
    const sbseries = scrollbarX.chart.series.push(
      am5xy.LineSeries.new(root, {
        xAxis: sbxAxis,
        yAxis: sbyAxis,
        valueYField: 'nav',
        valueXField: 'date',
      })
    )

    // Process and set chart data
    const chartData = priceHistory
      .map((item, index) => {
        if (!item) {
          console.warn(
            `PriceHistoryChart: Item at index ${index} is undefined.`
          )
          return null
        }
        if (!item.date) {
          console.warn(
            `PriceHistoryChart: Item at index ${index} is missing the 'date' property.`,
            item
          )
          return null
        }
        const parsedDate = new Date(item.date)
        if (isNaN(parsedDate.getTime())) {
          console.warn(
            `PriceHistoryChart: Invalid date format at index ${index}.`,
            item.date
          )
          return null
        }
        return {
          ...item,
          date: parsedDate.getTime(),
        }
      })
      .filter((item) => item !== null)

    if (chartData.length === 0) {
      console.warn('PriceHistoryChart: No valid chart data after processing.')
      return undefined
    }

    series.data.setAll(chartData)
    sbseries.data.setAll(chartData)

    // Set ARIA labels for accessibility
    amchartsSetAriaLabel('price-history-chart', 'Price History Chart')

    return () => {
      if (rootRef.current) {
        rootRef.current.dispose()
      }
    }
  }, [data])

  // Check data length outside effect for conditional rendering
  const priceHistory = get(data, 'priceHistory') || []

  return (
    <>
      {priceHistory.length === 0 ? (
        <NoDataContainer>
          <Typography variant="body1">{NO_DATA_MESSAGE}</Typography>
        </NoDataContainer>
      ) : (
        <FigureChart>
          <ChartContent id="price-history-chart" ref={chartRef} />
        </FigureChart>
      )}
    </>
  )
}

PriceHistoryChart.propTypes = {
  data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
}

export default PriceHistoryChart
